<template>
  <div class="shell-sidebar-wrapper">
    <div class="shell-sidebar-overlay"></div>
    <nav
      id="shell.sidebar"
      :class="['card shell-sidebar', {'shell-sidebar-peek': peek}, {'shell-sidebar-open': open}]"
      :style="sidebarTop === undefined ? undefined : {'--shell-sidebar-top': `${sidebarTop}px`}"
    >
      <TreeView
        :tree="tree"
        class="w-100 px-3 pb-3 pt-3 shell-sidebar-tree"
        role="group"
        :loading="loading"
        :active="active"
        @update:active="handleActive"
      >
        <template #nodeIcon="slotProps">
          <Icon
            v-if="slotProps.node.meta.icon"
            :class="`icon-${slotProps.node.meta.icon}`"
            :icon="`material-symbols:${slotProps.node.meta.icon}`"
          />
        </template>
        <template v-if="errorMessage" #error>
          <ErrorMessageAlert :error-message="errorMessage" class="me-3" />
        </template>
      </TreeView>
    </nav>
  </div>
</template>
<script setup lang="ts">
import {useMouse} from '@vueuse/core';
import {TreeNav} from '@/stores/apolloPlatform/cores/portalNavigation';
import {TreeViewNode} from '@/components/shared/custom/TreeView/TreeViewNode.vue';
const {tree} = defineProps<{loading: boolean; errorMessage?: string; tree: TreeNav}>();
const emit = defineEmits<{close: []; navigate: [meta: SpherePageMeta<SpherePageType>]}>();

const active = ref<TreeViewNode | undefined>();
const handleActive = (node: TreeViewNode) => {
  active.value = node;
  emit('navigate', node.meta);
};
const route = useRoute();
watch(
  () => route.name,
  () => (active.value = route.meta.node),
);

const open = defineModel<boolean>('open', {required: true});
const peek = defineModel<boolean>('peek', {required: true});

const {x} = useMouse({touch: false});
const sidebarEdge = ref(0);

// The sidebar overlaps the blue section's lower three quarters: the top bar and the page header. The page
// header scrolls with the page, so the sidebar moves up with it until only the top bar is left.
const sidebarTop = ref<number>();
let frame = 0;
const updateSidebarTop = () => {
  cancelAnimationFrame(frame);
  frame = requestAnimationFrame(() => {
    const headerBottom =
      document.querySelector('.shell-header')?.getBoundingClientRect().bottom ?? 0;
    const pageHeader = document.querySelector('#scrolling .page-header')?.getBoundingClientRect();
    const overlapTop = pageHeader ? pageHeader.bottom - pageHeader.height * 0.75 + 8 : 0; // 0.5rem
    sidebarTop.value = Math.max(headerBottom + 24, overlapTop); // Pinned: 1.5rem under the top bar
  });
};
let mutationObserver: MutationObserver | undefined;
onMounted(() => {
  const scrolling = document.getElementById('scrolling');
  scrolling?.addEventListener('scroll', updateSidebarTop, {passive: true});
  window.addEventListener('resize', updateSidebarTop);
  if (scrolling) {
    // The page header comes and goes (and changes size) with the page
    mutationObserver = new MutationObserver(updateSidebarTop);
    mutationObserver.observe(scrolling, {childList: true, subtree: true});
  }
  updateSidebarTop();
});
onBeforeUnmount(() => {
  cancelAnimationFrame(frame);
  document.getElementById('scrolling')?.removeEventListener('scroll', updateSidebarTop);
  window.removeEventListener('resize', updateSidebarTop);
  mutationObserver?.disconnect();
});

onMounted(() => {
  const el = document.getElementById('shell.sidebar')!;
  // Avoid calling this often as it forces a layout calculation
  const {width} = el.getBoundingClientRect();
  sidebarEdge.value = width + 16;
});

watch(x, (x) => {
  if (open.value === false) {
    if (peek.value === false && x < 16) {
      // Mouse enter the left side of your screen to peek!
      peek.value = true;
    } else if (peek.value === true && x > sidebarEdge.value) {
      // N.B. This includes the full screen height to help with clicking open!
      peek.value = false;
    }
  }
});
</script>
<style lang="scss" scoped>
@use '@/scss/breakpoints/breakpoints' as *;

.shell-sidebar-overlay {
  display: none;
  position: fixed;
  inset: 0;
  background-color: var(--bs-dark);
  z-index: 10;
}

.shell-sidebar {
  display: flex;
  flex-direction: column;
  position: fixed;
  width: var(--shell-sidebar-width);
  border-left: 0;
  border-top: 0;
  border-bottom: 0;
  border-radius: 0;
  border-color: var(--bs-border-color);
  top: var(--shell-header-height);
  bottom: 0;
  left: 0;
  transition-property: transform;
  transition-timing-function: ease;
  transition-duration: 200ms;
  z-index: 11;

  @include media-breakpoint-down(sm, $grid-breakpoints) {
    width: 100%;
    border: 0;
  }

  // One look, whether it is docked or peeking: a floating card with rounded corners.
  @include media-breakpoint-up(sm, $grid-breakpoints) {
    top: var(--shell-sidebar-top, calc(var(--shell-header-height) + 0.5rem));
    left: 0.5rem;
    bottom: 0.5rem;
    border: 0;
    border-radius: var(--bs-border-radius);
    overflow: hidden; // Clips the tree's scrollbar to the rounded corners
    box-shadow: var(--bs-box-shadow-lg);
  }
}

.shell-sidebar-open {
  transform: translate3d(0, 0, 0);
}

.shell-sidebar:not(.shell-sidebar-open) {
  transform: translate3d(calc(-100% - 2rem), 0, 0); // Also out of sight: its margin and shadow
}

.shell-sidebar-peek {
  @include media-breakpoint-up(sm, $grid-breakpoints) {
    transform: translate3d(0, 0, 0) !important; // Don't peak on mobile!
  }
}

:deep(.logo-ekco) {
  height: 100% !important;
  padding: 0.25rem 0;
}

/* Ben's style overwrites for TreeViewNode.vue */

/* Only style the active node's own label: an active (expanded) index page has visible children. */
:deep(.tree-node-active) {
  > .tree-node-label {
    // No highlight: the active node is only marked by its blue title.
    .tree-node-label-details,
    .tree-node-icon {
      color: var(--bs-primary) !important;
    }
  }
}

.shell-sidebar :deep(.shell-sidebar-tree) {
  // More specific than TreeView's own `font-size: 100%`
  font-size: 0.9375rem; // 15px, a touch under the 16px body text

  // Let the scrollbar sit in the right padding instead of narrowing the items: the gutter is always
  // reserved and the padding gives up the scrollbar's width (6px, see `custom-scrollbar`), so nothing shifts.
  scrollbar-gutter: stable;
  padding-right: calc(1rem - 6px) !important; // Beats the `px-3` utility
}

:deep(.shell-sidebar-tree > li > button) {
  .tree-node-label-details {
    padding: 0.5em 0;
  }

  .tree-node-icon,
  .tree-parent-node-toggle {
    margin-block-start: 0.5rem;
  }
}

:deep(.tree-node-icon) {
  margin-right: 0.5rem;
}
</style>
